import { useState } from 'react'
import PropTypes from 'prop-types'
import * as Yup from 'yup'
import classNames from 'classnames'
import { Formik, Form, Field } from 'formik'
import { useHistory, useLocation, useParams } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'

import Modal from 'components/Modal'
import { SpySidebar } from 'components/SpySidebar.js'
import Select from 'components/Select'
import CheckboxIndeterminate from 'components/CheckboxIndeterminate'
import RefundItems from 'components/RefundItems'
import Loader from 'components/Loader'

import { getCurrentUserEmail, getCurrentUserId } from 'store/Auth/selector'
import {
  fetchAllOrders,
  fetchRefundOrders,
  refundOrder,
} from 'store/Orders/actions'
import {
  getOrdersApiParamsConfig,
  getPendingRefundDetails,
} from 'store/Orders/selector'
import { refundReasons } from 'utils/resources'
import { getTotalRefund, getItemsWithoutSpecialType } from 'utils/Refund'
import { getLineItemsAndModifiers } from 'utils/Refund'
import { getCurrentVenueId } from 'store/Venues/selector'

const RefundComponent = ({ order, isLoading }) => {
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const { uuid } = useParams()
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  const { state } = order

  const userEmail = useSelector(getCurrentUserEmail)
  const userId = useSelector(getCurrentUserId)
  const pendingRefundDetails = useSelector((state) =>
    getPendingRefundDetails(state, uuid)
  )

  const venueUuid = useSelector(getCurrentVenueId)
  const ordersApiParamsConfig = useSelector(getOrdersApiParamsConfig)

  const isPendingRefundRoute = location.pathname.includes('/pending-refunds')

  // when completion_pending state is true refund button should be disabled
  const isCompletionPendingState = state === 'completion_pending'

  const isLineItemsDisabled =
    state === 'refunded' || pendingRefundDetails?.isPending === true

  // when we go to refund drawer from pending refunds section,
  // everything expect 'Refund' button should be disabled
  const isRefundButtonDisabled =
    isPendingRefundRoute && !isCompletionPendingState
      ? false
      : isLineItemsDisabled || isCompletionPendingState

  // return array of all line items without special type and their modifiers in order to
  // count their amount
  const itemsWithoutSpecialType = getItemsWithoutSpecialType(order?.lineItems)
  const lineItemsAndModifiers = getLineItemsAndModifiers(
    itemsWithoutSpecialType
  )
  const lineItemsAndModifiersUuids = lineItemsAndModifiers?.map(
    (lineItem) => lineItem.uuid
  )

  // when we go to refund from all orders, then all checkboxes should not be checked but when we go
  // from pendingRefunds, we have to check which checkboxes are checked from api property order?.mutations?.[0]?.mutationPayload?.lineItemUuids
  const lineItemsUuidsBasedOnRoute = isPendingRefundRoute
    ? pendingRefundDetails?.mutationLineItemUuids
    : []

  const initialValues = {
    refundReason: pendingRefundDetails?.reason ?? '',
    lineItemUuids: lineItemsUuidsBasedOnRoute ?? [],
  }

  const validationSchema = Yup.object().shape({
    refundReason: Yup.string().required(),
    lineItemUuids: Yup.array().min(1).required(),
  })

  // Entire order checkbox is checked when all lineItemsAndModifiersUuids are included in values.lineItemUuids
  const isCheckedEntireOrder = (values) => {
    return lineItemsAndModifiersUuids?.every((uuid) =>
      values?.lineItemUuids?.includes(uuid)
    )
  }

  // Entire order checkbox is in indeterminate state when at least one lineItemsAndModifiersUuid is included in values.lineItemUuids
  const isIndeterminateEntireOrder = (values) => {
    const atLeastOneIncluded = lineItemsAndModifiersUuids?.some((uuid) =>
      values?.lineItemUuids?.includes(uuid)
    )

    return !isCheckedEntireOrder(values) && atLeastOneIncluded
  }

  const onChangeEntireOrder = (value, setFieldValue) => {
    value
      ? setFieldValue('lineItemUuids', lineItemsAndModifiersUuids)
      : setFieldValue('lineItemUuids', [])
  }

  const onCloseModal = () => {
    isPendingRefundRoute
      ? history.push('/pending-refunds')
      : history.push('/orders')
  }

  const onClickReturn = () => {
    history.goBack()
  }

  const onClickSaveButton = () => {
    setShowValidationErrors(true)
  }

  const onSubmit = (values) => {
    const { lineItemUuids, refundReason } = values

    // refund reason payload should be extended with: <<reason_from_dropdown>> | Approved in Canopy by <<email>>, uuid: <<uuid>>>
    const extendedRefundReason = `${refundReason} | Approved in Canopy by ${userEmail}, uuid: ${userId}`

    dispatch(
      refundOrder(uuid, lineItemUuids, extendedRefundReason, userEmail, userId)
    )
      .then(() => {
        // when refund is done we have to refetch orders in order to update current order status on the index page
        isPendingRefundRoute
          ? dispatch(fetchRefundOrders(venueUuid))
          : dispatch(fetchAllOrders({ ...ordersApiParamsConfig, venueUuid }))
      })
      .then(() => {
        history.goBack()
      })
  }
  return (
    <Modal
      title="Refund"
      isReturnIcon
      onClickReturn={onClickReturn}
      onClose={onCloseModal}
      onRequestClose={onCloseModal}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <div className="modal-body">
          <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            {({ errors, values, setFieldValue, isSubmitting }) => (
              <Form className="item-settings">
                <section>
                  <h4 className="item-settings__header">Details</h4>
                  <div className="content-wrapper">
                    <Field
                      readonly={isLineItemsDisabled}
                      component={Select}
                      defaultValue={pendingRefundDetails?.reason}
                      name="refundReason"
                      label="Select Refund Reason"
                      nameClass={classNames({
                        '-error': errors.refundReason && showValidationErrors,
                      })}
                      options={refundReasons.map((reafundReason) => ({
                        uuid: reafundReason,
                        name: reafundReason,
                      }))}
                      handleChange={(reason) =>
                        setFieldValue('refundReason', reason)
                      }
                    />
                  </div>
                  <div className="content-wrapper -expanded-border">
                    <CheckboxIndeterminate
                      label={`Entire Order: $${order?.total}`}
                      checked={isCheckedEntireOrder(values)}
                      indeterminate={isIndeterminateEntireOrder(values)}
                      handleChange={({ checked }) =>
                        onChangeEntireOrder(checked, setFieldValue)
                      }
                      className={
                        isCheckedEntireOrder(values)
                          ? '-checkbox-indeterminate-checked'
                          : ''
                      }
                      disabled={isLineItemsDisabled}
                    />
                  </div>
                  <div className="content-wrapper -expanded-border">
                    <RefundItems
                      order={order}
                      disabled={isLineItemsDisabled}
                      itemsWithoutSpecialType={itemsWithoutSpecialType}
                    />
                  </div>
                  <div className="detailes -ml-0">
                    <div className="detailes__row">
                      <p className="detailes__info -highlighted">
                        Total Refund:
                      </p>
                      <p className="detailes__info -highlighted">
                        {getTotalRefund(
                          values,
                          itemsWithoutSpecialType,
                          isCheckedEntireOrder,
                          order.total
                        )}
                      </p>
                    </div>
                  </div>
                </section>
                <div className="btn-wrapper">
                  <button
                    className="btn -update"
                    onClick={onClickSaveButton}
                    disabled={
                      values.lineItemUuids.length === 0 ||
                      isRefundButtonDisabled ||
                      isSubmitting
                    }
                    type="submit"
                  >
                    Refund
                  </button>
                </div>
              </Form>
            )}
          </Formik>
          <SpySidebar settings={[{ text: 'Details', ref: null }]} />
        </div>
      )}
    </Modal>
  )
}

RefundComponent.propTypes = {
  order: PropTypes.object,
  isLoading: PropTypes.bool,
}

export default RefundComponent
